import type { ApproveOtherAccessRequestRequest, OtherAccessRequestPublic } from '@accounts/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type CSSProperties, useCallback, useState } from 'react';

import { STR } from '../../../../i18n';
import { approveOtherRequest, declineOtherRequest, listOtherRequests } from '../../../../lib/api';
import { relativeTime } from '../../../../lib/format';
import { queryKeys } from '../../../../lib/queryKeys';
import { showToast } from '../../../../lib/toast';
import { Callout, Card, EmptyRow } from '../../../../ui/Primitives';
import { quietButtonStyle, rowActionStyle } from '../../../../ui/styles';
import { pathLabel } from '../accessFormat';
import { errorText, useAccessNodesAll, useApplicationsList } from '../catalog/queries';

import { type OtherApproveDraft, OtherApproveDialog, OtherDeclineDialog, type OtherRequestView } from './OtherDialogs';

const ellipsis: CSSProperties = {
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const rowGrid: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '24px 96px minmax(120px,.9fr) minmax(140px,1fr) minmax(160px,1.3fr) auto',
  gap: 10,
  alignItems: 'center',
  padding: '7px 16px',
  borderBottom: '1px solid var(--border)',
  fontSize: 12.5,
};

function viewOf(request: OtherAccessRequestPublic): OtherRequestView {
  return {
    id: request.id,
    personName: request.user.name,
    what: request.what,
    whereNodeId: request.where?.nodeId ?? null,
    whereLabel: request.where === null ? null : pathLabel(request.where.path),
    why: request.why,
    createdAt: request.createdAt,
  };
}

function bodyOf(draft: OtherApproveDraft): ApproveOtherAccessRequestRequest {
  const base = {
    name: draft.row.name,
    nodeId: draft.row.nodeId,
    applicationId: draft.row.applicationId,
    requestable: draft.row.requestable,
    hrCanGrant: draft.row.hrCanGrant,
  };
  // `outsideSystem` exactly when there is no connected app; the server refuses it otherwise.
  return draft.record === null || draft.row.applicationId !== null
    ? base
    : { ...base, outsideSystem: draft.record };
}

interface DoneLine {
  id: string;
  label: string;
  tone: 'ok' | 'bad';
  person: string;
  what: string;
  at: string;
}

type Dialog = { kind: 'approve' | 'decline'; request: OtherAccessRequestPublic } | null;

/**
 * OTHER REQUESTS (spec row 13): requests for something not in the catalog. ADMINISTRATORS
 * ONLY — the Queue renders this lane only for an administrator, and the server's separate
 * read 403s HR, so HR never sees one here, on the bell, or in a bulk route.
 *
 * Each is decided one by one: Approve opens Add catalog row pre-filled from the request and
 * saves "Add row and grant to <person>" in one server transaction; Decline takes an
 * optional reason. They are never part of Approve all or the bulk bar (the server refuses
 * their ids there as well).
 */
export function OtherRequestsLane({ focusId }: { focusId: string | null }) {
  const queryClient = useQueryClient();
  const [closed, setClosed] = useState(false);
  const [dialog, setDialog] = useState<Dialog>(null);
  const [dialogError, setDialogError] = useState<string | null>(null);
  const [done, setDone] = useState<DoneLine[]>([]);

  const requests = useQuery({
    queryKey: queryKeys.accessOtherRequests('pending'),
    queryFn: () => listOtherRequests({ status: 'pending' }),
  });
  const nodes = useAccessNodesAll();
  const applications = useApplicationsList();

  const closeDialog = useCallback(() => {
    setDialog(null);
    setDialogError(null);
  }, []);

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.accessAll });
    void queryClient.invalidateQueries({ queryKey: queryKeys.allGrants });
    void queryClient.invalidateQueries({ queryKey: queryKeys.myNotifications });
  };

  const approve = useMutation({
    mutationFn: ({ request, draft }: { request: OtherAccessRequestPublic; draft: OtherApproveDraft }) =>
      approveOtherRequest(request.id, bodyOf(draft)),
    onSuccess: (result, { request }) => {
      setDone((current) => [
        {
          id: request.id,
          label: STR.otherRequests.doneApproved(result.item.name),
          tone: 'ok',
          person: request.user.name,
          what: request.what,
          at: new Date().toISOString(),
        },
        ...current,
      ]);
      closeDialog();
      showToast(STR.otherRequests.approved(result.item.name, request.user.name));
    },
    onError: (error) => {
      setDialogError(errorText(error));
    },
    onSettled: refresh,
  });

  const decline = useMutation({
    mutationFn: ({ request, reason }: { request: OtherAccessRequestPublic; reason: string | null }) =>
      declineOtherRequest(request.id, reason),
    onSuccess: (_result, { request, reason }) => {
      setDone((current) => [
        {
          id: request.id,
          label: STR.otherRequests.doneDeclined,
          tone: 'bad',
          person: request.user.name,
          what: request.what,
          at: new Date().toISOString(),
        },
        ...current,
      ]);
      closeDialog();
      showToast(STR.otherRequests.declined(request.user.name, reason !== null));
    },
    onError: (error) => {
      setDialogError(errorText(error));
    },
    onSettled: refresh,
  });

  const busy = approve.isPending || decline.isPending;
  const rows = requests.data?.data ?? [];
  const overdue = rows.filter((request) => request.overdue).length;

  // Nothing waiting and nothing decided here this visit: the lane stays out of the way.
  if (requests.isSuccess && rows.length === 0 && done.length === 0) return null;

  return (
    <Card>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '11px 16px',
          borderBottom: closed ? 'none' : '1px solid var(--border)',
          background: 'var(--surface-2)',
          fontSize: 13.5,
          fontWeight: 650,
        }}
      >
        <button
          type="button"
          className="tbtn"
          aria-expanded={!closed}
          onClick={() => {
            setClosed((value) => !value);
          }}
          style={{ ...quietButtonStyle, color: 'var(--text)', padding: '2px 6px' }}
        >
          {STR.otherRequests.laneTitle}{' '}
          <span className="tx-subtle" style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
            {rows.length}
          </span>
        </button>
        {overdue > 0 ? (
          <span className="tx-danger-strong" style={{ fontSize: 12, fontWeight: 700 }}>
            {STR.otherRequests.laneOverdue(overdue)}
          </span>
        ) : null}
      </div>
      {closed ? null : (
        <>
          <div style={{ padding: '8px 16px', borderBottom: '1px solid var(--border)' }}>
            <span className="tx-subtle" style={{ fontSize: 11.5 }}>
              {STR.otherRequests.laneHelp} {STR.otherRequests.oneByOne}
            </span>
          </div>
          {requests.isError ? (
            <div style={{ padding: 12 }}>
              <Callout tone="danger" role="alert">
                {STR.errors.couldNotLoad}
              </Callout>
            </div>
          ) : null}
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {done.map((entry) => (
              <li
                key={`done:${entry.id}`}
                data-off
                className="row"
                style={{
                  display: 'flex',
                  gap: 10,
                  alignItems: 'center',
                  padding: '7px 16px 7px 50px',
                  borderBottom: '1px solid var(--border)',
                  fontSize: 12,
                  background: 'var(--surface-2)',
                }}
              >
                <b
                  className={entry.tone === 'bad' ? 'tx-danger-strong' : 'tx-success-strong'}
                  style={{ fontWeight: 650 }}
                >
                  {entry.label}
                </b>
                <span className="tx-subtle">
                  {STR.access.queue.doneLine(entry.person, STR.otherRequests.laneLabel(entry.what), relativeTime(entry.at))}
                </span>
              </li>
            ))}
            {rows.length === 0 && done.length === 0 && !requests.isError ? (
              <li>
                <EmptyRow>{STR.otherRequests.laneEmpty}</EmptyRow>
              </li>
            ) : null}
            {rows.map((request) => (
              <li
                key={request.id}
                className="row"
                style={{
                  ...rowGrid,
                  ...(request.overdue ? { boxShadow: 'inset 3px 0 0 var(--bg-danger)' } : {}),
                  ...(request.id === focusId ? { background: 'var(--surface-2)' } : {}),
                }}
              >
                <span />
                <span
                  className={request.overdue ? 'tx-danger-strong' : 'tx-subtle'}
                  style={{
                    fontSize: 11.5,
                    fontWeight: request.overdue ? 700 : 400,
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {request.overdue
                    ? STR.access.queue.overdueAge(relativeTime(request.createdAt))
                    : relativeTime(request.createdAt)}
                </span>
                <span style={ellipsis}>
                  <span style={{ fontWeight: 650 }}>{request.user.name}</span>
                  <span className="tx-subtle" style={{ display: 'block', fontSize: 11.5, ...ellipsis }}>
                    {request.user.email}
                  </span>
                </span>
                <span style={ellipsis} title={request.what}>
                  {STR.otherRequests.laneLabel(request.what)}
                </span>
                <span style={{ ...ellipsis, fontSize: 12 }} title={request.why}>
                  <span className="tx-subtle">
                    {request.where === null
                      ? STR.otherRequests.notInCatalog
                      : STR.otherRequests.under(pathLabel(request.where.path))}
                    {', '}
                  </span>
                  {request.why.trim() === '' ? (
                    <span className="tx-subtle">{STR.otherRequests.noNote}</span>
                  ) : (
                    STR.otherRequests.whyLine(request.why)
                  )}
                </span>
                <span style={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                  <button
                    type="button"
                    className="tbtn"
                    style={rowActionStyle}
                    disabled={busy}
                    onClick={() => {
                      setDialogError(null);
                      setDialog({ kind: 'approve', request });
                    }}
                  >
                    {STR.access.queue.approve}
                  </button>
                  <button
                    type="button"
                    className="tbtn tbtn-danger"
                    style={rowActionStyle}
                    disabled={busy}
                    onClick={() => {
                      setDialogError(null);
                      setDialog({ kind: 'decline', request });
                    }}
                  >
                    {STR.otherRequests.decline}
                  </button>
                </span>
              </li>
            ))}
          </ul>
        </>
      )}

      {dialog?.kind === 'approve' ? (
        <OtherApproveDialog
          request={viewOf(dialog.request)}
          nodes={nodes.data ?? []}
          applications={applications.data?.data ?? []}
          pending={approve.isPending}
          error={dialogError}
          onCancel={closeDialog}
          onConfirm={(draft) => {
            approve.mutate({ request: dialog.request, draft });
          }}
        />
      ) : null}
      {dialog?.kind === 'decline' ? (
        <OtherDeclineDialog
          request={viewOf(dialog.request)}
          pending={decline.isPending}
          error={dialogError}
          onCancel={closeDialog}
          onConfirm={(reason) => {
            decline.mutate({ request: dialog.request, reason });
          }}
        />
      ) : null}
    </Card>
  );
}
